<template>
    <div v-click-away="hideDropdown">
        <label :for="id" class="block text-sm font-medium text-gray-700" v-if="label">{{ label }}<span v-show="required" class="font-bold text-teal">*</span></label>
        <div class="relative" :class="{
            'mt-1': hasLabel
        }">
            <button type="button" @click="activate = !activate" @keypress="handleKeypress($event)" class="relative w-full py-2 pl-3 pr-10 text-sm text-left bg-white border border-gray-200 rounded-md shadow-sm cursor-default focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal" :class="{
                'pointer-events-none bg-gray-100': disabled
            }">
                <span class="block truncate pointer-events-none">
                    {{ selectedLabel || '&nbsp;' }}
                </span>
                <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg class="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"></path></svg>
                </span>
            </button>

            <div class="absolute left-0 z-20 w-full mt-1 origin-top-left bg-white rounded-md shadow-lg" v-if="activate && options.length > 0">
                <div class="relative px-3 py-2" v-if="searchable">
                    <div class="rounded-md shadow-sm">
                        <div class="relative flex items-stretch flex-grow focus-within:z-10">
                            <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                            </div>
                            <input ref="searchBox" type="text" v-model="searchKey" class="block w-full pl-10 border-gray-200 rounded-md focus:ring-teal focus:border-teal sm:text-sm" placeholder="Search">
                        </div>
                    </div>
                </div>
                <ul tabindex="-1" class="py-1 overflow-auto text-base max-h-60 ring-1 ring-teal ring-opacity-5 focus:outline-none sm:text-sm" :class="{
                    'rounded-md': !searchable,
                    'rounded-b-md': searchable
                }">
                    <li role="option" class="relative py-2 pl-3 text-gray-900 cursor-pointer select-none pr-9 hover:bg-gray-50" v-for="(option, index) in filteredOptions" :key="index" @click="handleSelect(option)">
                        <span class="absolute inset-y-0 left-0 flex items-center pl-4">
                            <span class="block w-2 h-2 rounded-full" :class="{
                                'bg-gray-400': selectedValue != getOptionValue(option),
                                'bg-teal': selectedValue == getOptionValue(option)
                            }"></span>
                        </span>

                        <span class="block pl-6 font-normal truncate" :class="{'font-semibold': selectedValue == getOptionValue(option)}">
                            {{ getOptionLabel(option) }}
                        </span>

                        <span class="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-600" v-show="selectedValue == getOptionValue(option)">
                            <svg class="w-5 h-5" :class="{
                                'text-teal': selectedValue == getOptionValue(option)
                            }" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" /></svg>
                        </span>
                    </li>
                </ul>
            </div>
        </div>
        <input type="hidden" :name="id" :value="selectedValue">
    </div>
</template>

<script>
import { ref, onMounted, watch, nextTick, computed } from 'vue';

export default {
    props: {
        label: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default(){
                return 'select-field-' + Math.random().toString(36).substr(2, 9);
            }
        },
        name: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        required: {
            type: Boolean,
            default: false
        },
        options: {
            type: Array,
            default: () => []
        },
        labelBy: {
            type: String,
            default: 'key'
        },
        trackBy: {
            type: String,
            default: 'value'
        },
        modelValue: null,
        searchable: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit }){
        const activate      = ref(false)
        const selectedLabel = ref('')
        const selectedValue = ref('')
        const searchKey     = ref('')
        const searchBox     = ref(null)

        const filteredOptions = computed(() => {
            const options = _.filter(props.options, (option) => {
                let labels = _.lowerCase(getOptionLabel(option))
                let values = _.lowerCase(getOptionValue(option))

                return labels.includes(_.lowerCase(searchKey.value)) || values.includes(_.lowerCase(searchKey.value));
            })

            return options
        })

        onMounted(() => {
            init()
        })

        watch(() => props.modelValue, (nValue, oValue) => {
            if (nValue != oValue) {
                init()
            }
        })

        watch(() => activate.value, async (nValue, oValue) => {
            await nextTick()

            if(nValue && props.searchable && typeof searchBox.value !== null) {
                searchBox.value.focus()
            }
        })

        function init(){
            _.each(props.options, (option) => {
                if (getOptionValue(option) == props.modelValue) {
                    handleSelect(option);
                }
            });
        }

        function getOptionLabel(option){
            return (_.isObject(option) && !_.isUndefined(option[props.labelBy])) ? option[props.labelBy] : option;
        }

        function getOptionValue(option){
            return (_.isObject(option) && !_.isUndefined(option[props.trackBy])) ? option[props.trackBy] : option;
        }

        function handleSelect(option){
            selectedLabel.value = getOptionLabel(option);
            selectedValue.value = getOptionValue(option);

            emit('update:modelValue', selectedValue.value);
            emit('change', selectedValue.value);

            activate.value = false;
        }

        function hideDropdown(){
            activate.value = false;
        }

        function handleKeypress(evt){
            if (!props.searchable) {
                //searchKey.value = evt.key
            }

            activate.value = true
        }

        return {
            activate,
            selectedLabel,
            selectedValue,
            searchKey,
            searchBox,
            filteredOptions,
            getOptionLabel,
            getOptionValue,
            handleSelect,
            hideDropdown,
            handleKeypress
        };
    },
    computed: {
        hasLabel(){
            return this.label != '';
        },
    }
}
</script>

<style>

</style>
